import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Text,
  Box,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import UploadIcon from "@root/assets/icons/uploadIcon.svg";

interface ExcelUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport?: (file: File) => void | Promise<void>;
}

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const ACCEPTED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
];

export const ExcelUploadModal: React.FC<ExcelUploadModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState("");
  const [isImporting, setIsImporting] = useState(false);

  const validateAndSet = (file: File) => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setFileError(
        t(
          "subscriberManagement.invalidFileType",
          "Only .xlsx, .xls, or .csv files are allowed",
        ),
      );
      setSelectedFile(null);
      return;
    }
    setFileError("");
    setSelectedFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSet(file);
    // Reset input so the same file can be re-selected after removal
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSet(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError("");
  };

  const handleImport = async () => {
    if (!selectedFile || !onImport) return;
    setIsImporting(true);
    try {
      await onImport(selectedFile);
      handleClose();
    } catch {
      // parent handles error notification
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileError("");
    setIsImporting(false);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered >
      <ModalOverlay />
      <ModalContent borderRadius="20px" p="8px" bg="#FFFFFF">
        <ModalHeader fontSize="20px" fontWeight="700" color="#2F2F34">
          {t(
            "subscriberManagement.excelUploadTitle",
            "Import Subscribers via Excel",
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex direction="column" gap="16px">
            {/* Instructions */}
            <Text fontSize="13px" color="#798692">
              {t(
                "subscriberManagement.excelUploadDescription",
                "Upload an Excel or CSV file to bulk-import subscribers. Accepted formats: .xlsx, .xls, .csv",
              )}
            </Text>

            {/* Sample file download */}
            <Flex
              align="center"
              justify="space-between"
              bg="#F0FDF4"
              border="1px solid #BBF7D0"
              borderRadius="10px"
              p="12px 14px"
            >
              <Box>
                <Text fontSize="13px" fontWeight="600" color="#166534">
                  {t(
                    "subscriberManagement.sampleFileTitle",
                    "Need a template?",
                  )}
                </Text>
                <Text fontSize="11px" color="#15803D" mt="2px">
                  {t(
                    "subscriberManagement.sampleFileDescription",
                    "Download the sample Excel to see the required format.",
                  )}
                </Text>
              </Box>
              <Button
                as="a"
                href="/subscriber-list-sample.xlsx"
                download="subscriber-list-sample.xlsx"
                size="sm"
                bg="#16A34A"
                color="white"
                _hover={{ bg: "#15803D" }}
                fontSize="12px"
                onClick={(e) => e.stopPropagation()}
              >
                {t("subscriberManagement.downloadSample", "Download Sample")}
              </Button>
            </Flex>

            {/* Default password notice */}
            <Flex
              align="flex-start"
              gap="8px"
              bg="#FEF3C7"
              border="1px solid #FDE68A"
              borderRadius="10px"
              p="10px 12px"
            >
              <Text fontSize="12px" color="#92400E">
                <Text as="span" fontWeight="700">
                  {t(
                    "subscriberManagement.defaultPasswordLabel",
                    "Default password:",
                  )}
                </Text>{" "}
                <Text
                  as="span"
                  fontFamily="monospace"
                  bg="#FFFFFF"
                  px="6px"
                  py="1px"
                  borderRadius="4px"
                  border="1px solid #FDE68A"
                >
                  @kpec613081
                </Text>{" "}
                {t(
                  "subscriberManagement.defaultPasswordHint",
                  "is assigned to every newly imported subscriber / non-subscriber.",
                )}
              </Text>
            </Flex>

            {/* Required fields and value mapping */}
            <Box
              border="1px solid #E5E7EB"
              borderRadius="10px"
              p="12px 14px"
              bg="#FAFAFA"
            >
              <Text fontSize="13px" fontWeight="700" color="#2F2F34" mb="8px">
                {t(
                  "subscriberManagement.requiredFieldsTitle",
                  "What to fill in",
                )}
              </Text>
              <Box as="ul" pl="18px" mb="12px">
                <Box as="li" fontSize="12px" color="#4B5563" mb="4px">
                  <Text as="span" fontWeight="600" color="#2F2F34">
                    {t(
                      "subscriberManagement.fieldCompanyName",
                      "Subscriber name (Column C)",
                    )}
                  </Text>
                  {" — "}
                  {t(
                    "subscriberManagement.fieldCompanyNameDesc",
                    "the subscriber's name. It is also saved as the company name.",
                  )}
                </Box>
                <Box as="li" fontSize="12px" color="#4B5563" mb="4px">
                  <Text as="span" fontWeight="600" color="#2F2F34">
                    {t(
                      "subscriberManagement.fieldLoginId",
                      "Login ID (Column D)",
                    )}
                  </Text>
                  {" — "}
                  {t(
                    "subscriberManagement.fieldLoginIdDesc",
                    "a unique login for this subscriber (example: kpec_joongang).",
                  )}
                </Box>
                <Box as="li" fontSize="12px" color="#4B5563">
                  <Text as="span" fontWeight="600" color="#2F2F34">
                    {t(
                      "subscriberManagement.fieldSectionHeading",
                      "Group heading (Column B)",
                    )}
                  </Text>
                  {" — "}
                  {t(
                    "subscriberManagement.fieldSectionHeadingDesc",
                    "write this in the row above each list. It tells the system which group the rows below belong to. See the table below for allowed values.",
                  )}
                </Box>
              </Box>

              <Text fontSize="13px" fontWeight="700" color="#2F2F34" mb="4px">
                {t(
                  "subscriberManagement.valueMappingTitle",
                  "Group heading values",
                )}
              </Text>
              <Text fontSize="11px" color="#798692" mb="8px">
                {t(
                  "subscriberManagement.valueMappingHint",
                  "Type one of these exact words in the group heading row:",
                )}
              </Text>
              <Box as="ul" pl="18px">
                <Box as="li" fontSize="12px" color="#4B5563" mb="4px">
                  <Text as="span" fontWeight="600" color="#16A34A">
                    subscriber
                  </Text>
                  {" → "}
                  {t(
                    "subscriberManagement.mapSubscriber",
                    "Subscriber, Daily newspaper",
                  )}
                </Box>
                <Box as="li" fontSize="12px" color="#4B5563" mb="4px">
                  <Text as="span" fontWeight="600" color="#16A34A">
                    non subscriber
                  </Text>
                  {" → "}
                  {t(
                    "subscriberManagement.mapNonSubscriber",
                    "Non-subscriber, Daily newspaper",
                  )}
                </Box>
                <Box as="li" fontSize="12px" color="#4B5563" mb="4px">
                  <Text as="span" fontWeight="600" color="#16A34A">
                    subscriber weekly
                  </Text>
                  {" → "}
                  {t(
                    "subscriberManagement.mapSubscriberWeekly",
                    "Subscriber, Weekly newspaper",
                  )}
                </Box>
                <Box as="li" fontSize="12px" color="#4B5563">
                  <Text as="span" fontWeight="600" color="#16A34A">
                    weekly
                  </Text>
                  {" "}
                  <Text as="span" color="#798692">
                    {t("subscriberManagement.orLabel", "or")}
                  </Text>
                  {" "}
                  <Text as="span" fontWeight="600" color="#16A34A">
                    non subscriber weekly
                  </Text>
                  {" → "}
                  {t(
                    "subscriberManagement.mapWeekly",
                    "Non-subscriber, Weekly newspaper",
                  )}
                </Box>
              </Box>
            </Box>

            {/* Drop zone */}
            <Box
              border="2px dashed"
              borderColor={
                isDragging ? "#16A34A" : fileError ? "#E53E3E" : "#D1D5DB"
              }
              borderRadius="12px"
              bg={isDragging ? "#F0FDF4" : "#FAFAFA"}
              p="32px 24px"
              textAlign="center"
              cursor="pointer"
              transition="all 0.2s"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !selectedFile && inputRef.current?.click()}
              _hover={{ borderColor: "#16A34A", bg: "#F0FDF4" }}
            >
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(",")}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              {!selectedFile ? (
                <Flex direction="column" align="center" gap="12px">
                  <Box
                    bg="#DCFCE7"
                    borderRadius="full"
                    p="14px"
                    display="inline-flex"
                  >
                    <Image src={UploadIcon} boxSize="28px" />
                  </Box>
                  <Box>
                    <Text fontWeight="600" fontSize="14px" color="#2F2F34">
                      {t(
                        "subscriberManagement.dropFileHere",
                        "Drag & drop your file here",
                      )}
                    </Text>
                    <Text fontSize="12px" color="#798692" mt="4px">
                      {t("subscriberManagement.orBrowse", "or")}{" "}
                      <Text
                        as="span"
                        color="#16A34A"
                        fontWeight="600"
                        cursor="pointer"
                        _hover={{ textDecoration: "underline" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          inputRef.current?.click();
                        }}
                      >
                        {t("subscriberManagement.browseFile", "browse file")}
                      </Text>
                    </Text>
                  </Box>
                  <Text fontSize="11px" color="#B0B7C3">
                    {t(
                      "subscriberManagement.supportedFormats",
                      "Supported: .xlsx, .xls, .csv",
                    )}
                  </Text>
                </Flex>
              ) : (
                /* Selected file preview */
                <Flex align="center" justify="space-between" px="8px">
                  <Flex align="center" gap="12px">
                    <Box
                      bg="#DCFCE7"
                      borderRadius="8px"
                      p="10px"
                      display="inline-flex"
                    >
                      {/* Excel icon — green sheet */}
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="24" height="24" rx="4" fill="#16A34A" />
                        <text
                          x="4"
                          y="17"
                          fontSize="11"
                          fontWeight="bold"
                          fill="white"
                          fontFamily="Arial"
                        >
                          XLS
                        </text>
                      </svg>
                    </Box>
                    <Box textAlign="left">
                      <Text
                        fontWeight="600"
                        fontSize="13px"
                        color="#2F2F34"
                        maxW="260px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        title={selectedFile.name}
                      >
                        {selectedFile.name}
                      </Text>
                      <Text fontSize="11px" color="#798692">
                        {formatFileSize(selectedFile.size)}
                      </Text>
                    </Box>
                  </Flex>
                  {/* Remove button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    fontSize="12px"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                  >
                    {t("subscriberManagement.remove", "Remove")}
                  </Button>
                </Flex>
              )}
            </Box>

            {/* File error */}
            {fileError && (
              <Text fontSize="12px" color="#E53E3E">
                {fileError}
              </Text>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter width={"100%"} gap="12px">
          <Button
            width={"50%"}
            variant="outline"
            color="#1819B4"
            border="1px"
            borderColor="#1819B4"
            onClick={handleClose}
            isDisabled={isImporting}
          >
            {t("subscriberManagement.cancel", "Cancel")}
          </Button>
          <Button
            bg="#16A34A"
            color="white"
            width={"50%"}
            _hover={{ bg: "#15803D" }}
            isDisabled={!selectedFile || isImporting}
            isLoading={isImporting}
            onClick={handleImport}
          >
            {isImporting ? (
              <Spinner size="sm" />
            ) : (
              t("subscriberManagement.importButton", "Import")
            )}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
